import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";

import { api } from "../../lib/api";
import { formatInrLoose } from "../../lib/currency";
import { useAuth } from "../../providers/AuthProvider";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { ReportsDateRangeFilterBar } from "../components/reports/ReportsDateRangeFilterBar";
import { TableLayout, type TableColumn } from "../../components/dashboard";

type InventorySummaryRow = {
  product_id: string;
  product_name: string;
  total_stock_qty: string;
  stock_value_cost: string;
  stock_value_mrp: string;
};

function parseMoney(v: string) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export function ReportsInventoryPage() {
  const { token } = useAuth();

  const [fromDate, setFromDate] = useState<string | null>(null);
  const [toDate, setToDate] = useState<string | null>(null);

  const [rows, setRows] = useState<InventorySummaryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    const run = async () => {
      try {
        const res = await api.reports.getInventorySummary({
          token,
          filters: { from_date: fromDate, to_date: toDate },
        });
        if (cancelled) return;
        setRows((res.data as InventorySummaryRow[]) ?? []);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : "Failed to load inventory summary");
        setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [token, fromDate, toDate]);

  const totals = useMemo(() => {
    const totalStockQty = rows.reduce((s, r) => s + Number(r.total_stock_qty || 0), 0);
    const totalMrpValue = rows.reduce((s, r) => s + parseMoney(r.stock_value_mrp || "0"), 0);
    return { totalStockQty, totalMrpValue };
  }, [rows]);

  const columns: TableColumn<InventorySummaryRow>[] = useMemo(
    () => [
      {
        key: "product",
        header: "Product",
        render: (r) => (
          <div className="min-w-0 leading-tight">
            <div className="font-medium">{r.product_name}</div>
            <div className="font-mono text-xs text-muted-foreground">{r.product_id}</div>
          </div>
        ),
      },
      {
        key: "stock",
        header: "Stock qty",
        render: (r) => (
          <span className="block text-right tabular-nums">{Number(r.total_stock_qty || 0).toLocaleString()}</span>
        ),
      },
      {
        key: "cost",
        header: "Cost value",
        render: (r) => (
          <span className="block text-right tabular-nums">{formatInrLoose(parseMoney(r.stock_value_cost || "0"))}</span>
        ),
      },
      {
        key: "mrp",
        header: "MRP value",
        render: (r) => (
          <span className="block text-right tabular-nums">{formatInrLoose(parseMoney(r.stock_value_mrp || "0"))}</span>
        ),
      },
    ],
    [],
  );

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:flex-wrap lg:items-start lg:justify-between">
        <div>
          <h1 className="mb-1 text-2xl font-semibold text-foreground md:text-3xl">Inventory summary</h1>
          <p className="text-muted-foreground">
            Stock received (and remaining) grouped by product for the selected date range.
          </p>
        </div>
        <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
          <ReportsDateRangeFilterBar
            fromDate={fromDate}
            toDate={toDate}
            onChange={({ fromDate: nextFrom, toDate: nextTo }) => {
              setFromDate(nextFrom);
              setToDate(nextTo);
            }}
          />
          <div className="sm:self-end">
            <Link
              to="/reports"
              className="text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground"
            >
              Back to hub
            </Link>
          </div>
        </div>
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Products</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">{rows.length}</div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total stock qty</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">{totals.totalStockQty.toLocaleString()}</div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total MRP value</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">{formatInrLoose(totals.totalMrpValue)}</div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <CardTitle>Inventory table</CardTitle>
              <CardDescription>Sorted by stock value (MRP).</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-sm text-muted-foreground">Loading...</div>
          ) : rows.length === 0 ? (
            <div className="text-sm text-muted-foreground">No stock found.</div>
          ) : (
            <div className="min-w-0 rounded-md border md:border-0">
              <TableLayout columns={columns} rows={rows} />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

